/**
 * Zapier Integration Settings
 *
 * Zapier uses a webhook URL (from a "Webhooks by Zapier" or "Catch Hook" trigger in a Zap).
 * Velaro POSTs conversation data to that URL to trigger the Zap.
 *
 * Setup: In Zapier, create a Zap with "Webhooks by Zapier > Catch Hook" as the trigger.
 * Copy the Custom Webhook URL and paste it here.
 */
import React, { useEffect, useState } from "react";
import { PrimaryBlueButton } from "@velaro/velaro-component-lib";
import useApi from "@/hooks/useApi";

interface Config {
  configured: boolean;
  displayName: string;
  isEnabled: boolean;
  webhookUrlMasked: string;
}

function ZapierLogo() {
  return (
    <svg viewBox="0 0 100 100" className="w-10 h-10" fill="none">
      <rect width="100" height="100" rx="18" fill="#FF4A00" />
      <path d="M50 20 L62 44 L88 44 L68 60 L76 84 L50 68 L24 84 L32 60 L12 44 L38 44 Z"
        fill="white" />
    </svg>
  );
}

export default function ZapierPage() {
  const api = useApi();
  const [config, setConfig] = useState<Config | null>(null);
  const [webhookUrl, setWebhookUrl] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.messaging.get("Zapier/config")
      .then(r => r.ok ? r.json() : null)
      .then((data: Config | null) => {
        if (data) {
          setConfig(data);
          setDisplayName(data.displayName || "");
        } else {
          setConfig({ configured: false, displayName: "", isEnabled: false, webhookUrlMasked: "" });
        }
      })
      .catch(() => setConfig({ configured: false, displayName: "", isEnabled: false, webhookUrlMasked: "" }));
  }, []);

  async function save() {
    if (!webhookUrl.trim()) { setError("Webhook URL is required"); return; }
    setSaving(true);
    setError("");
    try {
      const res = await api.messaging.post("Zapier/config", {
        webhookUrl,
        displayName,
        isEnabled: true
      });
      if (res.ok) {
        setSaved(true);
        const updated = await api.messaging.get("Zapier/config").then(r => r.json());
        setConfig(updated);
        setWebhookUrl("");
        setTimeout(() => setSaved(false), 3000);
      } else {
        setError("Failed to save. Check the webhook URL and try again.");
      }
    } catch {
      setError("Network error. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <div className="w-14 h-14 border border-slate-300 rounded p-2 flex items-center justify-center">
          <ZapierLogo />
        </div>
        <div className="flex-1">
          <div className="font-semibold text-slate-800 text-lg">Zapier</div>
          <div className="text-slate-400 text-sm">
            Trigger Zapier Zaps from conversations and workflow nodes. Connect to 5,000+ apps — no code required.
          </div>
        </div>
        {config?.configured && (
          <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded-full px-3 py-1">
            <span className="w-2 h-2 rounded-full bg-green-500 inline-block" /> Connected
          </span>
        )}
      </div>

      {/* Status bar */}
      {config?.configured && (
        <div className="mb-6 rounded-lg border border-slate-200 p-4 bg-slate-50 text-sm">
          <div className="font-medium text-slate-700 mb-1">Active Zap</div>
          <div className="text-slate-500">{config.displayName || "Unnamed Zap"}</div>
          <div className="text-slate-400 font-mono text-xs mt-1">{config.webhookUrlMasked}</div>
        </div>
      )}

      {/* Feature cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        {[
          { icon: "⚡", title: "Workflow Node", desc: "Add a Zapier step in any workflow. Velaro fires the Zap with full conversation context." },
          { icon: "🔗", title: "5,000+ Apps", desc: "Connect to Salesforce, HubSpot, Gmail, Slack, Notion, Trello and thousands more." },
          { icon: "🎯", title: "Event Triggers", desc: "Trigger Zaps on conversation start, resolution, CSAT submission, or any workflow step." }
        ].map(f => (
          <div key={f.title} className="rounded-lg border border-slate-200 p-4">
            <div className="text-2xl mb-2">{f.icon}</div>
            <div className="font-medium text-slate-800 text-sm mb-1">{f.title}</div>
            <div className="text-xs text-slate-500">{f.desc}</div>
          </div>
        ))}
      </div>

      {/* Setup wizard */}
      <div className="mb-6 rounded-lg border border-orange-200 bg-orange-50 p-5">
        <div className="font-semibold text-orange-800 mb-3">Quick Setup (3 steps)</div>
        <ol className="space-y-2 text-sm text-orange-700">
          <li className="flex items-start gap-2">
            <span className="shrink-0 w-5 h-5 rounded-full bg-orange-200 text-orange-800 text-xs font-bold flex items-center justify-center">1</span>
            <span>In Zapier, create a new Zap and choose <strong>Webhooks by Zapier &gt; Catch Hook</strong> as the trigger.</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="shrink-0 w-5 h-5 rounded-full bg-orange-200 text-orange-800 text-xs font-bold flex items-center justify-center">2</span>
            <span>Copy the <strong>Custom Webhook URL</strong> Zapier shows and paste it in the field below.</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="shrink-0 w-5 h-5 rounded-full bg-orange-200 text-orange-800 text-xs font-bold flex items-center justify-center">3</span>
            <span>Add a <strong>Zapier</strong> node to any workflow in Velaro — it will POST conversation data and fire your Zap.</span>
          </li>
        </ol>
      </div>

      {/* Configuration form */}
      <div className="border border-slate-200 rounded-lg p-5">
        <div className="font-medium text-slate-800 mb-4">
          {config?.configured ? "Update Webhook URL" : "Connect Zapier"}
        </div>
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Zap Name (optional)</label>
            <input
              className="w-full border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
              placeholder="e.g. New Lead to Salesforce"
              value={displayName}
              onChange={e => setDisplayName(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Zapier Webhook URL *</label>
            <input
              className="w-full border border-slate-300 rounded px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-orange-400"
              placeholder="https://hooks.zapier.com/hooks/catch/..."
              value={webhookUrl}
              onChange={e => { setWebhookUrl(e.target.value); setError(""); }}
            />
          </div>
          {error && <p className="text-xs text-red-600">{error}</p>}
          {saved && <p className="text-xs text-green-600">Saved successfully.</p>}
        </div>
        <div className="mt-4">
          <PrimaryBlueButton label={saving ? "Saving..." : "Save"} onClick={save} prefixIcon={null} />
        </div>
      </div>

      {/* Payload reference */}
      <div className="mt-6 border border-slate-200 rounded-lg p-5">
        <div className="font-medium text-slate-800 mb-3">What Velaro sends to your Zap</div>
        <pre className="text-xs bg-slate-50 border border-slate-200 rounded p-3 overflow-auto text-slate-700">{`{
  "jobId": 12345,
  "siteId": 1001,
  "conversationId": 98765,
  "contactEmail": "visitor@example.com",
  "contactName": "John Smith",
  "agentName": "Sarah Lee",
  "sentiment": "positive",
  "workflowVariables": {
    "orderNumber": "ORD-1234",
    "issueType": "Billing"
  }
}`}</pre>
        <p className="text-xs text-slate-500 mt-2">
          Use any of these fields as inputs for your Zap actions. Map them to CRM fields, email templates, or any other app.
        </p>
      </div>
    </div>
  );
}
